import React from 'react';
import Config from 'config';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/hooks';
import { queryActions } from '../../../../../store/querySlice';
import IconInput from '../../../../common/fields/IconInput';
import { getFieldErrorStyles } from '../../style';
import IProperty from '../../../../../JSApi/constants/IProperty';
import { parseMask } from '../../../../../utils/util';
import useCombinedRefs from '../../../../../hooks/useCombinedRefs';
import { useFieldContext } from '../../fieldsStore/FieldContext';
import { TApiaFinderValue } from './types';
import { useApiaComponentContext } from '../../apiaComponent/ApiaComponentContext';
import useApiaFieldCommonProps from '../useApiaFieldCommonProps';
import { IApiaInput } from '../../../../../types';
import usePropsSelector from '../../../../../store/propsStore/usePropsSelector';
import { useApiaFormContext } from '../../page/ApiaForm';
import { isFinderValue } from './ApiaFinder';

const DEFAULT_INPUT_LENGTH = 255;
const ApiaFinderInput = React.forwardRef((_, ref) => {
  const dispatch = useAppDispatch();
  const {
    fireEvent,
    setValue,
    state: { value, isValid },
  } = useFieldContext<TApiaFinderValue | string>();
  const { fieldId, field } = useApiaComponentContext<IApiaInput>();
  const {
    attLabel,
    attName,
    properties,
    length,
    mask /*
    maskPlaceholder,
    decimalZeroes,
    cantDecimals, */,
  } = field;
  const { name } = properties;
  const props = usePropsSelector(fieldId);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const assignRefs = useCombinedRefs(inputRef, ref);

  const { isLoading, isOpen } = useAppSelector(
    (globalState) => ({
      isLoading: globalState.querySlice.queries[fieldId]?.loading,
      isOpen: globalState.querySlice.queries[fieldId]?.shouldShowModal,
    }),
    shallowEqual,
  );

  const [currentMask, setCurrentMask] = React.useState('');

  const inputProps = React.useMemo(
    () => ({
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
        setValue(ev.target.value);
      },
      onFocus: () => {
        setCurrentMask(mask ?? '');
      },
      onBlur: () => fireEvent('onChange'),
      value: typeof value === 'string' ? value : value?.showValue,
      key: `${fieldId}_${attName}`,
      id: fieldId,
      name: name || attName,
      maxLength: length || DEFAULT_INPUT_LENGTH,
      'aria-label': attLabel,
      title: props[IProperty.PROPERTY_TOOLTIP] || attLabel,
      required: props[IProperty.PROPERTY_REQUIRED],
      autoComplete: Config.AUTOCOMPLETE_OFF ? 'off' : 'on',
      sx: {
        ...getFieldErrorStyles(isValid),
        color: props[IProperty.PROPERTY_VALUE_COLOR],
        width: props[IProperty.PROPERTY_SIZE]
          ? `${props[IProperty.PROPERTY_SIZE] as string}px`
          : '100%',
      },
    }),
    [
      attLabel,
      attName,
      fieldId,
      fireEvent,
      isValid,
      length,
      mask,
      name,
      props,
      setValue,
      value,
    ],
  );

  const clickHandler = React.useCallback(() => {
    void dispatch(queryActions.loadStructure({ fieldId, qryId: props.qryId }));
  }, [dispatch, fieldId, props.qryId]);

  const {
    identifier: { className, ...identifier },
    isReadonly,
  } = useApiaFieldCommonProps();

  const { isFormReadOnly } = useApiaFormContext();

  if (isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]) {
    return (
      <Box {...identifier}>
        {isFinderValue(value) ? value.showValue : value}
      </Box>
    );
  }

  return (
    <IconInput
      aria-label={props[IProperty.PROPERTY_TOOLTIP] || attLabel}
      buttonProps={{
        disabled: props.disabled,
      }}
      className={`finderInput ${className ?? ''}`}
      disabled={props.disabled}
      hideButton={String(props.formData?.readOnly) === 'true'}
      inputProps={inputProps}
      inputRef={assignRefs as unknown as React.RefObject<HTMLInputElement>}
      isLoading={!!isLoading || isOpen}
      mask={parseMask(currentMask)}
      onClick={clickHandler}
      readOnly={isReadonly}
      {...identifier}
    />
  );
});

export default ApiaFinderInput;
